/* global dataLayer*/

import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  getPagePath,
  useSoyuzLocales,
  useSoyuzFlag
} from '@spotahome/soyuz/client';
import { useEcommerceManagerFactory } from '@spotahome/ecommerce-tracking';

import Tracking from '@spotahome/soyuz-tracking';

import internalLinkingModule from '@spotahome/marketplace-common/src/shapes/internalLinking';

import { configureMetricsActions } from '@spotahome/marketplace-common/src/graphql/mutations/tenantActions';

import { PricingProvider } from '@spotahome/marketplace-common/src/context/PricingContext';
import AdUrlService from '@spotahome/marketplace-common/src/utils/ad-url-service';

import { ListingShape } from '@spotahome/marketplace-common/src/shapes/listing';

import AdUnavailabilityService from '@spotahome/marketplace-common/src/LEGACY/javascripts/cornucopia/ads/ad-unavailability-service';
import ListingDatesValidatorService from '@spotahome/marketplace-common/src/LEGACY/javascripts/cornucopia/ads/listing-dates-validator-service';

import { formatDate } from '@spotahome/ui-library';

import { BOOKING_STATUSES } from '../../constants';

import Listing from '../Listing';
import NotFound from '../NotFound';
import { ListingNavigationProvider } from '../../context/ListingNavigationContext';

const getBookingStatus = (moveIn, moveOut, listing) => {
  const {
    availableFromDateIso8601,
    maxStay,
    minStay,
    moveInDatesRanges,
    unavailabilities
  } = listing;

  let status;
  const isAvailable =
    AdUnavailabilityService.isAvailable(moveIn, moveOut, unavailabilities) &&
    ListingDatesValidatorService.isMaxStayValid(moveIn, moveOut, maxStay) &&
    ListingDatesValidatorService.isMinStayValid(moveIn, moveOut, minStay) &&
    ListingDatesValidatorService.isMoveInDateValid(moveIn, moveInDatesRanges);

  if (availableFromDateIso8601 === null) {
    status = BOOKING_STATUSES.UNDEFINED_VALIDATION;
  } else if (!moveIn || !moveOut) {
    status = BOOKING_STATUSES.EMPTY_VALIDATION;
  } else if (isAvailable) {
    status = BOOKING_STATUSES.IS_AVAILABLE_VALIDATION;
  } else {
    status = BOOKING_STATUSES.NOT_AVAILABLE_VALIDATION;
  }

  return status;
};

const getInitialState = props => {
  const { listing, moveIn, moveOut } = props;
  let selectedMoveIn;
  let selectedMoveOut;
  if (moveIn) {
    const isInRange = ListingDatesValidatorService.isMoveInDateValid(
      moveIn,
      listing.moveInDatesRanges
    );

    if (!isInRange) {
      selectedMoveIn = formatDate(
        ListingDatesValidatorService.getMinAvailableDate(
          listing.moveInDatesRanges
        )
      );
    } else {
      selectedMoveIn = moveIn;
      selectedMoveOut = moveOut;
    }
  }

  return {
    moveIn: selectedMoveIn,
    moveOut: selectedMoveOut,
    bookingStatus: BOOKING_STATUSES.EMPTY_VALIDATION
  };
};

const HOTJAR_POTENTIAL_PERMANENT_BOOKING = 'potential_permanent_booking';

const trackHotjarPermanentContract = currentLocale => {
  Tracking.hotjar.recordTag(HOTJAR_POTENTIAL_PERMANENT_BOOKING);
  Tracking.hotjar.triggerEvent(
    `${HOTJAR_POTENTIAL_PERMANENT_BOOKING}_${currentLocale}`
  );
  Tracking.hotjar.triggerEvent('permanent_booking_listing');
};

const ShowRentableUnit = ({
  listing = null,
  areaSlug,
  moveIn = '',
  moveOut = '',
  isLogged = false,
  userId,
  isRoomsAggregator = false,
  listingCanonicalUrl,
  hideHeader = false,
  deviceType,
  deviceOS = '',
  internalLinking,
  landlordHomecards = []
}) => {
  const locales = useSoyuzLocales();
  const ecommerceManager = useEcommerceManagerFactory({
    sendInHouseAnalytics: useSoyuzFlag('SendInhouseSalesEvents').isOn()
  });
  const [state, setState] = useState(
    getInitialState({ listing, moveIn, moveOut })
  );

  useEffect(() => {
    if (!listing) {
      return;
    }

    configureMetricsActions();
    Tracking.actions.sendActionEvent(Tracking.actions.VIEW_PROPERTY, {
      listingId: listing.id
    });

    ecommerceManager.trackProductDetail({
      listing,
      isLogged,
      locale: locales.current,
      moveIn,
      moveOut
    });

    if (userId && typeof dataLayer !== 'undefined') {
      dataLayer.push({ userId });
    }

    if (listing.allowsPermanentContract) {
      trackHotjarPermanentContract(locales.current);
    }

    if (typeof dataLayer !== 'undefined' && Array.isArray(dataLayer)) {
      dataLayer.push({
        remarketing_listing_id: listing.id,
        remarketing_listing_pagetype: 'Productpage',
        remarketing_listing_total_value: listing.pricing.price
      });
    }
  }, []);

  const handleBookingDatesChange = async ({
    moveIn: newMoveIn,
    moveOut: newMoveOut
  }) => {
    const bookingStatus = getBookingStatus(newMoveIn, newMoveOut, listing);

    setState(prevState => {
      const newState = {};

      if (newMoveIn) {
        newState.moveIn = newMoveIn;
      }

      newState.moveOut = newMoveOut;

      return {
        ...prevState,
        ...newState,
        bookingStatus
      };
    });

    AdUrlService.setLocationUrl({
      ...(newMoveOut ? { 'move-in': newMoveIn } : null),
      ...(newMoveOut ? { 'move-out': newMoveOut } : null)
    });

    if (newMoveIn) {
      Tracking.actions.sendActionEvent(Tracking.actions.SELECT_DATES, {
        listingId: listing.id,
        moveIn: newMoveIn,
        moveOut: `${newMoveOut}`
      });
    }
  };

  const hasGAP = () =>
    parseInt(listing.bookingAcceptanceMaxDays, 10) >= 0 ||
    parseInt(listing.bookingAcceptanceMinDays, 10) > 0;

  const isWholeApartment = () =>
    listing.type === 'whole' || listing.type === 'studio';

  const mapSearchHref = getPagePath('marketplace.free-search', {
    params: {
      locationString: areaSlug
    },
    query: {
      'move-in': state.moveIn,
      'move-out': state.moveOut
    }
  });

  if (!listing) {
    return <NotFound exploreButtonHref={mapSearchHref} />;
  }

  const {
    allowsTemporalContract,
    allowsPermanentContract,
    pricing,
    bookingConditions,
    id,
    plan,
    cityId
  } = listing;

  return (
    <section className="l-main-section">
      <PricingProvider
        allowsTemporalContract={allowsTemporalContract}
        allowsPermanentContract={allowsPermanentContract}
        listingPricing={pricing}
        bookingConditions={bookingConditions}
        listingId={id}
        listingPlan={plan}
        checkInDate={state.moveIn}
        checkOutDate={state.moveOut}
        cityId={cityId}
      >
        <ListingNavigationProvider>
          <Listing
            hasGAP={hasGAP()}
            isRoomsAggregator={isRoomsAggregator}
            isWholeApartment={isWholeApartment()}
            listing={listing}
            listingCanonicalUrl={listingCanonicalUrl}
            locale={locales.current}
            moveIn={state.moveIn}
            moveOut={state.moveOut}
            onBookingDatesChange={handleBookingDatesChange}
            hideHeader={hideHeader}
            bookingStatus={state.bookingStatus}
            deviceType={deviceType}
            deviceOS={deviceOS}
            internalLinking={internalLinking}
            landlordHomecards={landlordHomecards}
          />
        </ListingNavigationProvider>
      </PricingProvider>
    </section>
  );
};

ShowRentableUnit.propTypes = {
  areaSlug: PropTypes.string.isRequired,
  isRoomsAggregator: PropTypes.bool,
  listing: PropTypes.shape(ListingShape),
  listingCanonicalUrl: PropTypes.string.isRequired,
  moveIn: PropTypes.string,
  moveOut: PropTypes.string,
  isLogged: PropTypes.bool,
  hideHeader: PropTypes.bool,
  userId: PropTypes.string.isRequired,
  deviceType: PropTypes.oneOf(['smartphone', 'tablet', 'desktop']).isRequired,
  deviceOS: PropTypes.string,
  internalLinking: PropTypes.arrayOf(PropTypes.shape(internalLinkingModule))
    .isRequired,
  landlordHomecards: PropTypes.arrayOf(PropTypes.object)
};

export default ShowRentableUnit;
